import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IVideoClip, ResourceType, ResourceStatus, IAudioGeneration } from '@packages/global';
import { SpeakerWaveIcon, XMarkIcon, ListBulletIcon, MusicalNoteIcon, ScissorsIcon, TrashIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { videoClipResourceApi } from '../../../services/user/videoClipResource.api';
import { videoClipApi } from '../../../services/user/videoclip.api';
import { useDialog } from '../../../hooks/useDialog';
import { useToast } from '../../../stores/toastStore';
import AudioUpload from '../../common/AudioUpload';
import AudioSelector from '../../common/AudioSelector';
import AudioPlayer from '../../common/AudioPlayer';
import { useVideoEditor } from '../context/VideoEditorContext';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { FFmpegExporter } from '../../../utils/ffmpegExporter';
import { FFmpegCDNLoader } from '../../../utils/ffmpegCDNLoader';

interface AudioTabProps {
  videoClip: IVideoClip;
  onVideoClipUpdate?: (updatedClip: IVideoClip) => void;
  refreshVideoClip?: (clipId: string) => Promise<void>;
  onRequestClose?: () => void;
}

export default function AudioTab({ videoClip, onVideoClipUpdate, refreshVideoClip, onRequestClose }: AudioTabProps) {
  const { t } = useTranslation(['editor', 'common']);
  const [showAudioSelector, setShowAudioSelector] = useState<'script' | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExtractingBgm, setIsExtractingBgm] = useState(false);
  const [isExtractingAudio, setIsExtractingAudio] = useState(false);
  const [extractAudioProgress, setExtractAudioProgress] = useState('');

  const { confirm } = useDialog();
  const { success, error, warning } = useToast();
  const { state } = useVideoEditor();
  const { episode, editConfig } = state;

  const checkResourceStatus = async () => {
    try {
      const status = await videoClipResourceApi.getResourcesStatus(videoClip.id);
      const scriptAudioResources = status.filter((r: any) => r.resourceType === ResourceType.SCRIPT_AUDIO)
        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      const scriptAudioResource = scriptAudioResources.length > 0 ? scriptAudioResources[0] : null;

      const newGeneratingState = scriptAudioResource?.status === ResourceStatus.GENERATING || scriptAudioResource?.status === ResourceStatus.PENDING;
      const wasGenerating = isGenerating;

      setIsGenerating(newGeneratingState);

      if (wasGenerating && !newGeneratingState && refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }
    } catch (error) {
      console.error('检查资源状态失败:', error);
    }
  };

  useEffect(() => {
    checkResourceStatus();
  }, [videoClip.id]);

  useEffect(() => {
    if (isGenerating) {
      const interval = setInterval(checkResourceStatus, 2000);
      return () => clearInterval(interval);
    }
  }, [isGenerating]);

  const handleSelectAudio = async (audio: IAudioGeneration) => {
    if (!showAudioSelector) return;

    try {
      const audioUrl = audio.resultAudioUrl;
      if (!audioUrl) {
        warning(t('editor:common.applyFailed'));
        return;
      }

      await handleUploadAudio(audioUrl, showAudioSelector);
      setShowAudioSelector(null);
      success(t('editor:common.audioSelectSuccess'));
    } catch (err) {
      console.error('选择音频失败:', err);
      error(t('editor:common.applyFailed'));
    }
  };

  const handleUploadAudio = async (url: string, type: 'script' | 'soundEffect') => {
    try {
      const updateData = type === 'script'
        ? { scriptAudioUrl: url }
        : { soundEffectAudioUrl: url };

      await videoClipApi.updateVideoClip(videoClip.id, updateData);

      // 刷新 videoClip 数据
      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }
      success(t('editor:common.audioSetSuccess'));
    } catch (err) {
      console.error('设置音频失败:', err);
      error(t('editor:common.applyFailed'));
    }
  };

  const handleClearAudio = async (type: 'script' | 'soundEffect') => {
    const confirmed = await confirm(t('editor:audioTab.clearAudioConfirm'), t('editor:imagesTab.confirmClear'), 'warning');
    if (!confirmed) return;

    try {
      const updateData = type === 'script'
        ? { scriptAudioUrl: '' }
        : { soundEffectAudioUrl: '' };

      await videoClipApi.updateVideoClip(videoClip.id, updateData);

      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }
      success(t('editor:common.audioCleared'));
    } catch (err) {
      console.error('清除音频失败:', err);
      error(t('editor:common.applyFailed'));
    }
  };

  // 从背景音乐提取当前分镜对应时间段的音频
  const handleExtractFromBgm = async () => {
    const bgmUrl = episode?.bgmUrl;
    if (!bgmUrl) {
      warning(t('editor:audioTab.noBgm'));
      return;
    }
    if (!editConfig) {
      warning(t('editor:editConfigNotLoaded'));
      return;
    }

    // 找到当前分镜在时间线上的位置
    const clipInfo = editConfig.clips.find(c => c.clipId === videoClip.id);
    if (!clipInfo) {
      warning(t('editor:audioTab.noTimelineInfo'));
      return;
    }

    const { startTime, duration } = clipInfo;

    setIsExtractingBgm(true);
    try {
      // 加载 FFmpeg（复用共享实例）
      const exporter = new FFmpegExporter();
      await exporter.load();

      // 直接访问共享 FFmpeg 实例
      const ffmpeg = (FFmpegExporter as any).sharedFFmpeg as FFmpeg;
      if (!ffmpeg) throw new Error('FFmpeg 未初始化');

      // 下载 BGM 文件
      ffmpeg.writeFile('bgm_extract_src.mp3', await fetchFile(bgmUrl));

      // 截取对应时间段
      const outputFile = 'bgm_clip_extract.mp3';
      await ffmpeg.exec([
        '-ss', startTime.toString(),
        '-i', 'bgm_extract_src.mp3',
        '-t', duration.toString(),
        '-c:a', 'libmp3lame',
        '-q:a', '2',
        outputFile
      ]);

      // 读取截取结果
      const data = await ffmpeg.readFile(outputFile);
      // FFmpeg 返回 Uint8Array，通过 slice 创建新的 ArrayBuffer 以避免 SharedArrayBuffer 问题
      const uint8Array = new Uint8Array(data as Uint8Array);
      const blob = new Blob([uint8Array], { type: 'audio/mpeg' });

      // 清理临时文件
      try { await ffmpeg.deleteFile('bgm_extract_src.mp3'); } catch { }
      try { await ffmpeg.deleteFile(outputFile); } catch { }

      // 上传到服务器
      const formData = new FormData();
      formData.append('audio', blob, `bgm_clip_${videoClip.id}.mp3`);

      const response = await fetch('/api/user/upload/audio', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      const result = await response.json();
      if (!result.success || !result.data?.url) {
        throw new Error(result.message || 'Upload failed');
      }

      // 更新配音 URL
      await videoClipApi.updateVideoClip(videoClip.id, { scriptAudioUrl: result.data.url });
      if (refreshVideoClip) await refreshVideoClip(videoClip.id);
      success(t('editor:audioTab.extractedFromBgm'));
    } catch (err: any) {
      console.error('提取背景音乐失败:', err);
      error(`${t('common:failed')}：${err.message || t('common:retry')}`);
    } finally {
      setIsExtractingBgm(false);
    }
  };

  // 从视频中分离音频
  const handleExtractAudioFromVideo = async () => {
    if (!videoClip.videoUrl) {
      warning(t('editor:audioTab.noVideo'));
      return;
    }

    setIsExtractingAudio(true);
    setExtractAudioProgress(t('common:loading'));

    try {
      const loader = new FFmpegCDNLoader((p) => setExtractAudioProgress(p.message));
      const { ffmpeg } = await loader.loadFFmpeg();

      setExtractAudioProgress(t('editor:exportOverlay.downloadResources'));
      const videoData = await fetchFile(videoClip.videoUrl);
      await ffmpeg.writeFile('input.mp4', videoData);

      // 获取当前 clip 的剪辑信息
      const clipInfo = editConfig?.clips.find(c => c.clipId === videoClip.id);
      const trimStart = clipInfo?.trimStart || 0;
      const trimEnd = clipInfo?.trimEnd;

      setExtractAudioProgress(t('editor:exportOverlay.processClips'));

      // 构建 ffmpeg 命令，根据剪辑信息裁剪音频
      const ffmpegArgs = ['-i', 'input.mp4'];

      // 如果有裁剪开始时间，添加 -ss 参数
      if (trimStart > 0) {
        ffmpegArgs.push('-ss', trimStart.toString());
      }

      // 如果有裁剪结束时间，添加 -to 参数
      if (trimEnd !== undefined && trimEnd > trimStart) {
        ffmpegArgs.push('-to', trimEnd.toString());
      }

      // 添加音频提取参数
      ffmpegArgs.push('-vn', '-acodec', 'libmp3lame', '-ab', '192k', 'output.mp3');

      await ffmpeg.exec(ffmpegArgs);

      const audioData = await ffmpeg.readFile('output.mp3');
      if (!audioData || (audioData as Uint8Array).length === 0) {
        throw new Error(t('editor:audioTab.noVideo'));
      }

      const audioBlob = new Blob([new Uint8Array(audioData as Uint8Array)], { type: 'audio/mpeg' });
      const audioFile = new File([audioBlob], 'extracted_audio.mp3', { type: 'audio/mpeg' });

      setExtractAudioProgress(t('common:upload') + '...');
      const formData = new FormData();
      formData.append('audio', audioFile);

      const response = await fetch('/api/user/upload/audio', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      const result = await response.json();
      if (!result.success || !result.data?.url) {
        throw new Error(result.message || 'Upload failed');
      }

      await videoClipApi.updateVideoClip(videoClip.id, { scriptAudioUrl: result.data.url });
      if (refreshVideoClip) await refreshVideoClip(videoClip.id);

      try { await ffmpeg.deleteFile('input.mp4'); } catch { }
      try { await ffmpeg.deleteFile('output.mp3'); } catch { }
      ffmpeg.terminate();

      success(t('editor:audioTab.audioSeparateSuccess'));
    } catch (err: any) {
      console.error('分离音频失败:', err);
      error(err.message || t('editor:common.applyFailed'));
    } finally {
      setIsExtractingAudio(false);
      setExtractAudioProgress('');
    }
  };
  return (
    <div className="flex flex-col h-full max-h-[90vh] bg-bg-surface text-gray-50">
      <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-border-subtle">
        <div className="flex items-baseline gap-3 min-w-0">
          <h2 className="text-lg font-semibold shrink-0">{t('editor:previewArea.audio')}</h2>
          <p className="text-sm text-gray-400 truncate">{videoClip.title || t('editor:audioTab.noDubbingAudio')}</p>
        </div>
        <button
          onClick={onRequestClose}
          className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-border-subtle bg-bg-elevated hover:bg-border-default text-gray-200 shrink-0"
          title={t('common:close', { defaultValue: '关闭' })}
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-5">
          <div className="flex flex-col gap-4">
            {videoClip.script && (
              <div className="rounded-2xl border border-border-subtle bg-bg-surface p-5">
                <div className="flex items-center gap-2 mb-3 text-gray-300">
                  <ChatBubbleLeftRightIcon className="w-4 h-4 text-indigo-400" />
                  <h3 className="text-sm font-medium">{t('editor:audioTab.dubbingScript')}</h3>
                </div>
                <div className="rounded-xl bg-bg-form border border-border-subtle p-4">
                  <p className="text-base leading-7 text-gray-100">{videoClip.script}</p>
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-border-subtle bg-bg-surface p-5">
              <div className="flex items-center gap-2 mb-3 text-gray-300">
                <SpeakerWaveIcon className="w-4 h-4 text-indigo-400" />
                <h3 className="text-sm font-medium">{t('editor:audioTab.dubbingAudio')}</h3>
              </div>

              <div className="rounded-xl bg-bg-form border border-border-subtle p-4 min-h-[96px] flex items-center">
                {videoClip.scriptAudioUrl ? (
                  <div className="flex items-center gap-3 w-full">
                    <SpeakerWaveIcon className="h-5 w-5 text-indigo-400 flex-shrink-0" />
                    <AudioPlayer key={videoClip.scriptAudioUrl} src={videoClip.scriptAudioUrl} className="w-full h-8 flex-1" />
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">{t('editor:audioTab.noDubbingAudio')}</p>
                )}
              </div>

              {isExtractingAudio && extractAudioProgress && (
                <p className="mt-3 text-xs text-indigo-300">{extractAudioProgress}</p>
              )}
            </div>

            <div className="rounded-2xl border border-border-subtle bg-bg-surface p-5 mt-auto">
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowAudioSelector('script')}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-4 py-2 bg-bg-elevated hover:bg-border-default disabled:bg-bg-elevated disabled:cursor-not-allowed text-gray-50 text-sm rounded-lg transition-colors"
                >
                  <ListBulletIcon className="w-4 h-4" />
                  {t('common:select')}
                </button>

                <AudioUpload
                  episodeId={videoClip.episodeId}
                  onUploadSuccess={(url) => handleUploadAudio(url, 'script')}
                  mode="button"
                  buttonText={t('common:upload')}
                  buttonClassName="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-border-subtle bg-bg-surface p-5">
              <h3 className="text-sm font-medium text-gray-300 mb-4">{t('editor:audioTab.extractToolsTitle')}</h3>

              <div className="space-y-5">
                <div>
                  <button
                    onClick={handleExtractFromBgm}
                    disabled={isGenerating || isExtractingBgm || !episode?.bgmUrl}
                    title={t('editor:audioTab.extractBgm')}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-purple-700 hover:bg-purple-600 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm rounded-xl transition-colors"
                  >
                    {isExtractingBgm ? (
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <MusicalNoteIcon className="w-4 h-4" />
                    )}
                    {isExtractingBgm ? t('editor:audioTab.extractingBgm') : t('editor:audioTab.extractBgm')}
                  </button>
                  <p className={`mt-2 text-xs leading-5 ${episode?.bgmUrl ? 'text-gray-400' : 'text-amber-400'}`}>
                    {episode?.bgmUrl ? t('editor:audioTab.extractBgmHelp') : t('editor:audioTab.noBgm')}
                  </p>
                </div>

                <div>
                  <button
                    onClick={handleExtractAudioFromVideo}
                    disabled={isGenerating || isExtractingAudio || !videoClip.videoUrl}
                    title={t('editor:audioTab.separateAudio')}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm rounded-xl transition-colors"
                  >
                    {isExtractingAudio ? (
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <ScissorsIcon className="w-4 h-4" />
                    )}
                    {isExtractingAudio ? t('editor:audioTab.separatingAudio') : t('editor:audioTab.separateAudio')}
                  </button>
                  <p className={`mt-2 text-xs leading-5 ${videoClip.videoUrl ? 'text-gray-400' : 'text-amber-400'}`}>
                    {videoClip.videoUrl ? t('editor:audioTab.separateAudioHelp') : t('editor:audioTab.separateAudioDisabledHint')}
                  </p>
                </div>
              </div>
            </div>

            {videoClip.scriptAudioUrl && (
              <div className="rounded-2xl border border-red-900/40 bg-red-950/15 p-5">
                <button
                  onClick={() => handleClearAudio('script')}
                  disabled={isGenerating}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded-xl transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                  {t('common:clear')}
                </button>
                <p className="mt-2 text-xs leading-5 text-gray-400">{t('editor:audioTab.clearAudioConfirm')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 生成中的遮罩层 */}
      {isGenerating && (
        <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center gap-4 text-white z-10">
          <div className="w-16 h-16 border-4 border-gray-400 border-t-indigo-500 rounded-full animate-spin" />
          <p className="text-lg font-medium">{t('editor:audioTab.generatingAudio')}</p>
        </div>
      )}

      <AudioSelector
        isOpen={!!showAudioSelector}
        onClose={() => setShowAudioSelector(null)}
        onSelect={handleSelectAudio}
      />
    </div>
  );
}
